import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import merchantService from 'services/api/merchants.api';
import { GetMerchantsParams, Merchant, UseMerchantsOptions } from 'pages/MerchantPortal/merchant-portal.types';

type PaginationProps = {
  totalPageNumber: number;
  currentPageNumber: number;
  rowsPerPageOptions: number[];
  currentRowsPerPage: number;
  handlePageChange: (page: number) => void;
};

type UseMerchantsResult = {
  paginationProps: PaginationProps;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  merchants: Merchant[];
};

export default function useMerchants(includeOffers = false, options: UseMerchantsOptions = {}): UseMerchantsResult {
  const { sortby, sortorder, initialOffset = 0, limit = 10 } = options;

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [queryOptions, setQueryOptions] = useState({ sortby, sortorder, initialOffset, limit });

  if (
    queryOptions.sortby !== sortby ||
    queryOptions.sortorder !== sortorder ||
    queryOptions.initialOffset !== initialOffset ||
    queryOptions.limit !== limit
  ) {
    setQueryOptions({ sortby, sortorder, initialOffset, limit });
    setCurrentPageNumber(1);
  }

  const offset = initialOffset + (currentPageNumber - 1) * limit;
  const queryKey = ['merchants', { includeOffers, sortby, sortorder, limit, offset }];

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const params: GetMerchantsParams = {
        includeOffers,
        limit,
        offset,
        sortby,
        sortorder,
      };
      return merchantService.getMerchants(params);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const totalPageNumber = Math.ceil((data?.pagination?.totalRecords || 0) / limit);
  const merchants = data?.data ?? [];

  const paginationProps = {
    totalPageNumber,
    currentPageNumber,
    rowsPerPageOptions: [limit],
    currentRowsPerPage: limit,
    handlePageChange: setCurrentPageNumber,
  };

  return {
    paginationProps,
    isLoading,
    isError,
    isFetching,
    merchants,
  };
}
